import React, { useEffect, useRef, useState } from "react";
import { Box, Skeleton, useTheme } from "@mui/material";
import CompactMarketMovers from "./MarketMovers/CompactMarketMovers";
import NewsList from "./News/NewsList";
import OverviewMarketBand, { BAND_CELL_GAP, BAND_CELL_HEIGHT } from "./OverviewMarketBand";
import { riseIn, SECTION_HEADER_HEIGHT } from "./overviewPanel";
import {
  CONDENSED_MAX_ROWS,
  CONDENSED_ROW_HEIGHT,
  PANEL_ROW_GAP,
  PANEL_ROW_HEIGHT,
  PANEL_SLOTS,
} from "./economicCalendarSelection";
import { useAnalytics } from "../../hooks/useAnalytics";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { useTouchLayout } from "../../hooks/useTouchLayout";
import { isNative } from "../../lib/platform";

// The dashboard home opens on the wordmark, then the market band — the index
// cards and the economic calendar side by side — and then the front page
// proper: the user's own companies on the left, the news column on the
// right. Every section is the same headerless idiom: a floating header over
// open cards. Sector cards and the heatmap live on the Sectors page.
//
// The wordmark is DESKTOP-ONLY: MobileTopBar already carries the logo, and it
// is mounted below lg on web (ProtectedLayout) and at every width on native
// (NativeTabLayout) — so the same condition, not a plain breakpoint, decides
// whether the page repeats it.

// First mount of the session gets the full page skeleton (caches are cold);
// later mounts have warm query caches, so the two gate frames paint nothing
// instead — a skeleton flashed over instantly-available content reads as a
// glitch. Each section still shows its own skeleton if its query is cold.
let hasMountedThisSession = false;

// The band skeleton's geometry derives from the content's own constants, so
// the real band lands exactly where the placeholder stood instead of
// shifting everything under it. The markets grid is two columns (three
// rows) below md and three (two rows) above; the calendar is the condensed
// three-row cut on touch layouts and the four-slot rail otherwise; at lg the
// two columns stretch to the taller one.
const marketsHeight = (rows: number): number =>
  SECTION_HEADER_HEIGHT + rows * BAND_CELL_HEIGHT + (rows - 1) * BAND_CELL_GAP;
const CALENDAR_CONDENSED_HEIGHT =
  SECTION_HEADER_HEIGHT + CONDENSED_MAX_ROWS * CONDENSED_ROW_HEIGHT;
const CALENDAR_RAIL_HEIGHT =
  SECTION_HEADER_HEIGHT + PANEL_SLOTS * PANEL_ROW_HEIGHT + (PANEL_SLOTS - 1) * PANEL_ROW_GAP;
const BAND_LG_HEIGHT = Math.max(marketsHeight(2), CALENDAR_RAIL_HEIGHT);

const DashboardOverviewTab: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { trackFeature } = useAnalytics();
  const { isLargeDesktop } = useBreakpoint();
  const isTouch = useTouchLayout();
  const showWordmark = isLargeDesktop && !isNative();

  // Mirror of ViewTickerLoader's skeleton-first gate, for the BACK direction:
  // navigating back re-mounts this whole page, and with warm caches the tree
  // used to mount synchronously inside the route transition — the tap looked
  // ignored. One painted skeleton frame goes up first, then the real content.
  const [contentReady, setContentReady] = useState(false);
  const mountedAtRef = useRef(performance.now());
  const warmMount = useRef(hasMountedThisSession);
  useEffect(() => {
    hasMountedThisSession = true;
    let inner: number | undefined;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setContentReady(true));
    });
    return () => {
      cancelAnimationFrame(outer);
      if (inner !== undefined) cancelAnimationFrame(inner);
    };
  }, []);

  // Perf telemetry: how long the dashboard takes from mount to real content
  // on screen.
  useEffect(() => {
    if (!contentReady) return;
    const raf = requestAnimationFrame(() => {
      trackFeature("perf_dashboard_mount", {
        duration_ms: Math.round(performance.now() - mountedAtRef.current),
      });
    });
    return () => cancelAnimationFrame(raf);
  }, [contentReady, trackFeature]);

  const skeletonBg = isDark ? "rgba(255,255,255,0.09)" : "rgba(0,0,0,0.09)";

  if (!contentReady) {
    if (warmMount.current) return null;
    return (
      <>
        {/* Mirrors the page: wordmark, the band's two panels, then the
            watchlist and news panels */}
        {showWordmark && (
          <Skeleton
            variant="rounded"
            animation="wave"
            height={44}
            sx={{
              mt: 1,
              mb: 3,
              mx: "auto",
              maxWidth: 220,
              borderRadius: 1,
              bgcolor: skeletonBg,
            }}
          />
        )}
        {/* The band: markets matrix and calendar — side by side at lg,
            stacked below — each reserving the height its content will take */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.9fr) minmax(0, 1fr)" },
            gap: { xs: 2, md: 2.5 },
            mb: { xs: 4, md: 5 },
          }}
        >
          <Skeleton
            variant="rounded"
            animation="wave"
            sx={{
              height: {
                xs: marketsHeight(3),
                md: marketsHeight(2),
                lg: isTouch ? marketsHeight(2) : BAND_LG_HEIGHT,
              },
              borderRadius: 1.5,
              bgcolor: skeletonBg,
            }}
          />
          <Skeleton
            variant="rounded"
            animation="wave"
            sx={{
              height: isTouch
                ? CALENDAR_CONDENSED_HEIGHT
                : { xs: CALENDAR_CONDENSED_HEIGHT, md: CALENDAR_RAIL_HEIGHT, lg: BAND_LG_HEIGHT },
              borderRadius: 1.5,
              bgcolor: skeletonBg,
            }}
          />
        </Box>
        {/* The front page: the watchlist and news panels */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 2fr) minmax(0, 3fr)" },
            gap: 3,
            alignItems: "start",
          }}
        >
          <Skeleton
            variant="rounded"
            animation="wave"
            height={360}
            sx={{ borderRadius: 1.5, bgcolor: skeletonBg }}
          />
          <Skeleton
            variant="rounded"
            animation="wave"
            height={420}
            sx={{ borderRadius: 1.5, bgcolor: skeletonBg, display: { xs: "none", lg: "block" } }}
          />
        </Box>
      </>
    );
  }

  return (
    <>
      {/* The wordmark sets the page, then the market band leads the
          content. Where the top bar already shows the logo, the band leads
          on its own rather than repeating it. */}
      {showWordmark && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            pt: 1,
            pb: 3,
            ...riseIn(0),
          }}
        >
          <img
            src={isDark ? "/insightLongLogoPrimaryWhiteText.svg" : "/insightLongLogoPrimary.svg"}
            alt="Insight Analytics"
            style={{ height: 44, width: "auto", maxWidth: "100%" }}
          />
        </Box>
      )}
      <OverviewMarketBand />

      {/* The front page, mirrored 40/60: the watchlist holds the left,
          news leads on the right — the same seam as the markets band above.
          On phones the user's own companies come first. */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 2fr) minmax(0, 3fr)" },
          gap: 3,
          // Each column keeps its natural height: the news panel ends on its
          // last story rather than stretching past it when the watchlist
          // runs longer.
          alignItems: "start",
        }}
      >
        <Box data-tour="market-movers" sx={{ minWidth: 0, ...riseIn(140) }}>
          <CompactMarketMovers />
        </Box>
        <Box sx={{ minWidth: 0, ...riseIn(210) }}>
          <NewsList />
        </Box>
      </Box>

      {/* Bottom spacing */}
      <Box sx={{ height: 48 }} />
    </>
  );
};

export default DashboardOverviewTab;
